import { ReactNode } from "react";

import { faFilter } from "@fortawesome/pro-regular-svg-icons";
import { faFilter as faFilterSolid } from "@fortawesome/pro-solid-svg-icons";
import {
  Listbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions,
} from "@headlessui/react";
import { twJoin } from "tailwind-merge";

import { Button } from "src/base-components/Button";
import {
  FixedDropDownKey,
  FixedPositionedDropdown,
  FixedPositionedDropdownProps,
} from "src/base-components/FixedPositionedDropDown";
import { Icon } from "src/base-components/Icon";
import { Dropdown, DropdownItem } from "src/design-system/Dropdown";

type Props<T extends string> = {
  isFiltering: boolean;
  onResetRequest: () => void;
  description: string;
  selected: T | undefined;
  onSelect: (key: T, e: React.MouseEvent<HTMLSpanElement>) => void;
  dataLoc?: string;
  onApply?: () => void;
  isApplied?: boolean;
  showFooter?: boolean;
} & Pick<FixedPositionedDropdownProps<string, T>, "elements" | "placement">;

export const ColumnFilter = <T extends string>({
  isFiltering,
  onResetRequest,
  description,
  dataLoc,
  onApply,
  isApplied,
  showFooter = true,
  ...props
}: Props<T>) => (
  <FixedPositionedDropdown
    buttonClassName="border-0 bg-transparent"
    dataLoc={dataLoc}
    elements={props.elements}
    multiple={false}
    placement={props.placement ?? "bottom end"}
    renderButtonValue={() => (
      <Icon
        color={
          isFiltering ? "text-indigo-500" : "text-gray-500 hover:text-gray-700"
        }
        dataLoc={`${dataLoc}-button`}
        icon={isFiltering ? faFilterSolid : faFilter}
        size="2xs"
      />
    )}
    renderFooter={(close) =>
      showFooter && (
        <div className="sticky bottom-0 mt-0.5 flex translate-y-2 cursor-default justify-between border-gray-200 border-t bg-white p-4">
          <Button
            dataLoc={`${dataLoc}-reset-button`}
            disabled={!isFiltering}
            size="sm"
            variant="secondary"
            onClick={() => {
              onResetRequest();
              close();
            }}
          >
            Reset
          </Button>
          <Button
            dataLoc={`${dataLoc}-apply-button`}
            disabled={isApplied}
            size="sm"
            onClick={() => {
              onApply?.();
              close();
            }}
          >
            Apply
          </Button>
        </div>
      )
    }
    renderHeader={() => (
      <div className="sticky top-0 flex w-full -translate-y-2 cursor-default items-center justify-between bg-white px-5 py-3.5 pt-5.5">
        <h3 className="font-inter-semibold-13px">{description}</h3>
        {isFiltering && !showFooter && (
          <button
            className="font-inter-medium-13px text-indigo-500"
            onClick={onResetRequest}
          >
            Reset
          </button>
        )}
      </div>
    )}
    renderValue={({ value, selected, disabled }) => {
      return (
        <DropdownItem
          className={twJoin(disabled && "cursor-default text-gray-300")}
          data-selected={selected ? "true" : undefined}
        >
          {value}
        </DropdownItem>
      );
    }}
    selected={props.selected}
    onSelect={props.onSelect}
  />
);

type MultiProps<T extends FixedDropDownKey> = {
  isFiltering: boolean;
  onResetRequest: () => void;
  headline: string;
  defaultValue: T[] | undefined;
  onApply: (key: T[]) => void;
  dataLoc?: string;
  options: FixedPositionedDropdownProps<ReactNode, T>["elements"];
  values: T[];
  onChange: (values: T[]) => void;
  isApplied: boolean;
  hideFooter?: boolean;
};

export const ColumnMultiValueFilter = <T extends FixedDropDownKey>({
  isFiltering,
  onResetRequest,
  options,
  onApply,
  headline,
  dataLoc,
  values,
  onChange,
  isApplied,
  hideFooter = false,
}: MultiProps<T>) => {
  return (
    <Listbox value={values} multiple onChange={onChange}>
      <ListboxButton data-loc={dataLoc}>
        <Icon
          color={
            isFiltering
              ? "text-indigo-500"
              : "text-gray-500 hover:text-gray-700"
          }
          icon={isFiltering ? faFilterSolid : faFilter}
          size="2xs"
        />
      </ListboxButton>
      <ListboxOptions
        anchor={{
          to: "bottom end",
          gap: 10,
          padding: 12,
        }}
        as={Dropdown}
        data-loc={`${dataLoc}-dropdown`}
        transition
      >
        <div className="flex h-10 items-center px-4 pb-2.5 font-inter-semibold-13px text-gray-800">
          {headline}
        </div>
        {options.map(({ key, value }) => (
          <ListboxOption key={key} as={DropdownItem} value={key}>
            {value}
          </ListboxOption>
        ))}
        {!hideFooter && (
          <div className="mt-2.5 flex cursor-default justify-between border-gray-200 border-t bg-white px-4 pt-4 pb-2">
            <Button
              dataLoc={`${dataLoc}-reset-button`}
              disabled={values?.length === 0}
              size="sm"
              variant="secondary"
              onClick={onResetRequest}
            >
              Reset
            </Button>
            <Button
              dataLoc={`${dataLoc}-apply-button`}
              disabled={isApplied}
              size="sm"
              onClick={() => onApply(values)}
            >
              Apply
            </Button>
          </div>
        )}
      </ListboxOptions>
    </Listbox>
  );
};
